import React, { useState } from 'react';
import {
  Button,
  Divider,
  ActionIcon,
  Collapse,
  Title,
  LoadingOverlay,
} from '@mantine/core';
import Chevron from 'react-chevron';

interface ItemList {
  id: number;
  name: string;
  [key: string]: any;
}

interface ItemTool {
  index: number; // order on the bar
  function: (item: any) => void;
  icon: React.ReactNode;
  onHoverColor?: 'red' | 'blue' | 'green';
}

interface DisplayAttribute {
  index: number; // order
  attribute: keyof ItemList;
}

interface Props {
  title: string; // title of the list near the chevron
  list?: ItemList[]; // list that will be displayed
  onItemClick?: (id: number) => void;
  createNewItem?: () => void; // onClick function that will be called when pressed on button to create new item
  createNewItemButtonText?: string; // text on the button
  itemToolBar?: ItemTool[];
  loading?: boolean;
  collapsed?: boolean;
  displayAttributes?: DisplayAttribute[];
}

const renderListItemContent = (
  item: ItemList,
  displayAttributes: DisplayAttribute[]
) => (
  <div className="flex items-center h-full ml-auto">
    {displayAttributes
      .sort((a, b) => a.index - b.index)
      .map(displayAttribute => (
        <div key={displayAttribute.attribute} className="mr-4">
          {item[displayAttribute.attribute]}
        </div>
      ))}
  </div>
);

export const DropDownList: React.FC<Props> = ({
  title,
  list,
  onItemClick,
  createNewItem,
  createNewItemButtonText,
  itemToolBar,
  loading = false,
  collapsed = false,
  displayAttributes = [],
}: Props) => {
  const [reversed, setReversed] = useState(!collapsed);

  const onHoverColorMapper = {
    red: 'hover:text-red-600',
    blue: 'hover:text-blue-600',
    green: 'hover:text-green-600',
  };

  return (
    <div className="h-full relative flex flex-col">
      <LoadingOverlay
        visible={loading}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />

      <div className="flex items-center mr-4 ml-4">
        <Title order={4}>{title}</Title>
        <div
          onClick={() => setReversed(!reversed)}
          className="w-10 h-10 flex justify-center items-center hover:cursor-pointer hover:text-blue-600"
        >
          <Chevron reversed={reversed} direction={'down'} />
        </div>
        {createNewItem && ( // Check if new item can be added
          <Button
            key="new-service"
            size="md"
            onClick={createNewItem}
            className="text-lg ml-auto"
          >
            {createNewItemButtonText}
          </Button>
        )}
      </div>
      <Divider className="mt-2" />

      <Collapse in={reversed} className="flex-1 overflow-auto">
        <ul>
          {list?.map((item, index) => (
            <li key={item.id}>
              <div className="h-14 flex items-center">
                {onItemClick ? (
                  <a
                    onClick={() => onItemClick(item.id)}
                    className="flex items-center w-full h-full hover:cursor-pointer hover:bg-slate-50 pl-4"
                  >
                    {item.name}
                    {renderListItemContent(item, displayAttributes)}
                  </a>
                ) : (
                  <div className="flex items-center w-full h-full p-4">
                    {item.name}
                    {renderListItemContent(item, displayAttributes)}
                  </div>
                )}

                {itemToolBar && (
                  <div className="flex justify-center items-center gap-4 ml-4 mr-4">
                    {itemToolBar
                      .sort((a, b) => a.index - b.index)
                      .map((ItemTool, index) => (
                        <ActionIcon
                          key={index}
                          variant="transparent"
                          size="md"
                          className={`${ItemTool.onHoverColor ? onHoverColorMapper[ItemTool.onHoverColor] : onHoverColorMapper.red}`}
                          color="black"
                          onClick={() => ItemTool.function(item)}
                        >
                          {ItemTool.icon}
                        </ActionIcon>
                      ))}
                  </div>
                )}
              </div>
              {index !== list.length - 1 && <Divider />}
            </li>
          ))}
        </ul>
      </Collapse>
    </div>
  );
};
